export interface IGreenState {
  msiEnvironmentalCredentialsData: IMsiEnvironmentalCredentialsState | null;
  greenSummaryData: IGreenSummaryState | null;
  rollingAerValuesData: IRollingAerValuesState | null;
  emissionsOfEcaSeaAreasData: IEmissionsOfEcaSeaAreasState | null;
  cohortEmissionsOfEcaSeaAreasData: ICohortEmissionsOfEcaSeaAreasState | null;
  emissionsOfOperationalPhasesData: IEmissionsOfOperationalPhasesState | null;
  cohortEmissionsOfOperationalPhasesData: ICohortEmissionsOfOperationalPhasesState | null;
  vesselCiiAlignmentsData: IVesselCiiAlignmentsState | null;
  fleetCiiAlignmentData: IFleetCiiAlignmentState | null;
  vesselsEtsAndFuelEuEmissionsData: IVesselsEtsAndFuelEuEmissionsState | null
  cohortVesselsEtsAndFuelEuEmissionsData: ICohortVesselsEtsAndFuelEuEmissionsState | null
  vesselsMainEngineFuelConsumesData: IVesselsMainEngineFuelConsumesState | null
  cohortVesselsMainEngineFuelConsumesData: ICohortVesselsMainEngineFuelConsumesState | null
  vesselKeyMetricsDateBasedData: IVesselKeyMetricsDateBasedState | null
  cohortVesselKeyMetricsDateBasedData: ICohortVesselKeyMetricsDateBasedState | null
}

// # region msi environmental credentials
export interface IMsiEnvironmentalCredentialsState {
  msiEnvironmentalCredentialsData: IMsiSingleEnvironmentalCredentials | null
  msiEnvironmentalCredentialsDatas: IMsiSingleEnvironmentalCredentials[] | null
  portfolioEnvironmentalCredentials: IMsiPortfolioEnvironmentalCredentials | null
  loading: boolean
  error: Error | null
}

export interface IMsiEnvironmentalCredentials {
  singleEnvironmentalCredentials: IMsiSingleEnvironmentalCredentials[] | null;
  portfolioEnvironmentalCredential: IMsiPortfolioEnvironmentalCredentials | null;
}

export interface IMsiSingleEnvironmentalCredentials {
  imoNumber: number
  aerEstimators: IAerEstimator[]
  aerCarbonIntensityIndicators: IAerCarbonIntensityIndicator[]
  aerEexiCompliances: IAerEexiCompliance[]
  vesselLocationChart: IVesselLocationChart
  aerTrajectories: IAerTrajectory[]
  ciiValues: ICiiValues[]
}

export interface IMsiPortfolioEnvironmentalCredentials {
  environmentalCredential: IEnvironmentalCredential[]
  portfolioLocationChartData: IPortfolioLocationChartData,
  portfolioEnvironmentalCredential: IPortfolioEnvironmentalCredential
  aerYear: number
}

export interface IEnvironmentalCredential {
  vesselName: string
  imoNumber: number
  sector: string
  size: number
  yearOfDelivery: number

  estimatedAERTtW: number
  estimatedAERWtW: number
  ciiScore: number

  estimatedCIIRatingYear1: string
  estimatedCIIRatingYear2: string
  estimatedCIIRatingYear3: string
  estimatedCIIRatingYear4: string
  vesselWeighting: number
  requiredCarbonIntensityPpTrajectoryMinimum: number
  climateAlignmentPpTrajectoryMinimum: string
  requiredCarbonIntensityPpTrajectoryStriving: number
  climateAlignmentPpTrajectoryStriving: string
  estimatedEEXI: number
  requiredEEXI: number
  compliant: string

  ciiValues: ICiiValues[]
}

export interface IAerEstimator {
  year: number | string
  msiEstimate: number
  ppTrajectoryMinimum: number
  ppTrajectoryStriving: number
  alignmentMin?: number
  alignmentStriving?: number
  ciiValue?: string
  aer: number,
  aerWtw: number,
  ciiScore: number,
}

export interface ICiiValues {
  year: number
  currentCii?: string
  rollingAerCgDist: number,
  rollingAerCgDistE: number,
  rollingCiiScore: number,
}

export interface IAerCarbonIntensityIndicator {
  year: number
  ciiValue: string
}

export interface IAerEexiCompliance {
  eexiName: string
  eexiValue: number
}

export interface IVesselLocationChart {
  title: string
  yAxis: string
  expressions: IExpressions
  chartDataItemList: IChartDataItemList[]
}

export interface IExpressions {
  estimated: string
  ppTrajectoryMinimum: string
  ppTrajectoryStriving: string
  estimatedRecentValues: string
}

export interface IChartDataItemList {
  year: number
  estimated?: number
  ppTrajectoryMinimum: number
  ppTrajectoryStriving: number
  estimatedRecentValues?: number
}

export interface IAerTrajectory {
  year: number
  value: number
  vesselId: number
  trajectoryName: string
}

export interface IPortfolioLocationChartData {
  chartDataItemList: IChartDataItemList[]
  expressions: IExpressions
  title: string
}

export interface IChartDataItemList {
  year: number
  portfolioAer: number
  ppTrajectoryMinimum: number
  ppTrajectoryStriving: number
}

export interface IExpressions {
  portfolioAer: string
  ppTrajectoryMinimum: string
  ppTrajectoryStriving: string
}

export interface IPortfolioEnvironmentalCredential {
  ppTrajectoryMinimum: string
  ppTrajectoryStriving: string
}
// # endregion

// # region green summary
export interface IGreenSummaryState {
  greenSummaryData: IGreenSummary[] | null,
  loading: boolean,
  error: Error | null
}

export interface IGreenSummary {
  sectorId?: number
  sectorName?: string
  shipCount?: number
  avgSize?: number
  sizeUnit?: string
  avgAge?: number
  avgClimateAlignment: number
}

// # endregion

// # region rolling aer

export interface IRollingAerValuesState {
  rollingAerValuesData: IRollingAerValues[]
  loading: boolean
  error: Error | null
}

export interface IRollingAerValues {
  signalDateTime: string
  rollingAerCgDist: number
  rollingAerCgDistWtw: number
  rollingCiiScore: number
  aValue: number
  bValue: number
  cValue: number
  dValue: number
  eValue: number
}

// # endregion

// # region emissions
export interface IEmissionsOfEcaSeaAreasState {
  emissionsOfEcaSeaAreasData: IEmissionsOfEcaSeaAreas[]
  loading: boolean
  error: Error | null
}

export interface ICohortEmissionsOfEcaSeaAreasState {
  cohortEmissionsOfEcaSeaAreasData: IEmissionsOfEcaSeaAreas[]
  loading: boolean
  error: Error | null
}

export interface IEmissionsOfEcaSeaAreas {
  ecaSeaArea: string
  sumOfEmission: number
}

export interface IEmissionsOfOperationalPhasesState {
  emissionsOfOperationalPhasesData: IEmissionsOfOperationalPhases[]
  loading: boolean
  error: Error | null
}

export interface ICohortEmissionsOfOperationalPhasesState {
  cohortEmissionsOfOperationalPhasesData: IEmissionsOfOperationalPhases[]
  loading: boolean
  error: Error | null
}

export interface IEmissionsOfOperationalPhases {
  operationalPhase: string
  sumOfEmission: number
}

// # endregion

// # region vessel cii
export interface IVesselCiiAlignmentsState {
  vesselCiiAlignmentData: IVesselCiiAlignments[]
  loading: boolean
  error: Error | null
}

export interface IVesselCiiAlignments {
  imoNumber: number
  currentAer: number,
  currentAerWtw: number,
  ciiScore: number,
  ciiReference: number,
  ciiAlignment: number,
  currentCii: string,
  year: number
}

export interface IFleetCiiAlignmentState {
  fleetCiiAlignmentData: IFleetCiiAlignment | null
  loading: boolean
  error: Error | null
}

export interface IFleetCiiAlignment {
  ciiAlignment: number
  calculatedImoCount: number
  imoCount: number
  coveragePercentage: number
}

export interface IVesselsCurrentCiiValuesCountsState {
  vesselsCurrentCiiValuesCountsData: IVesselsCurrentCiiValuesCounts[] | null
  loading: boolean
  error: Error | null
}

export interface ICohortVesselsCurrentCiiValuesCountsState {
  cohortVesselsCurrentCiiValuesCountsData: IVesselsCurrentCiiValuesCounts[] | null
  loading: boolean
  error: Error | null
}

export interface IVesselsCurrentCiiValuesCounts {
  currentCii: string
  countOfCurrentCii: number
}

// # endregion

// # region vessels ets fueleu emissions

export interface IVesselsEtsAndFuelEuEmissionsState {
  vesselsEtsAndFuelEuEmissionsData: IVesselsEtsAndFuelEuEmissions[] | null
  loading: boolean
  error: Error | null
}

export interface ICohortVesselsEtsAndFuelEuEmissionsState {
  cohortVesselsEtsAndFuelEuEmissionsData: IVesselsEtsAndFuelEuEmissions[] | null
  loading: boolean
  error: Error | null
}

export interface IVesselsEtsAndFuelEuEmissions {
  etsTrueSum: number
  etsFalseSum: number
  fuelEuTrueSum: number
  fuelEuFalseSum: number
}

// # endregion

// # region vessel main engine fuel consumes

export interface IVesselsMainEngineFuelConsumesState {
  vesselsMainEngineFuelConsumesData: IVesselsMainEngineFuelConsumes[] | null
  loading: boolean
  error: Error | null
}

export interface ICohortVesselsMainEngineFuelConsumesState {
  cohortVesselsMainEngineFuelConsumesData: IVesselsMainEngineFuelConsumes[] | null
  loading: boolean
  error: Error | null
}

export interface IVesselsMainEngineFuelConsumes {
  mainEngineFuelType: string
  mainEngineEmission: number
}

export interface IVesselKeyMetricsDateBasedState {
  vesselKeyMetricsDateBasedData: IVesselKeyMetricsDateBased[] | null
  loading: boolean
  error: Error | null
}

export interface ICohortVesselKeyMetricsDateBasedState {
  cohortVesselKeyMetricsDateBasedData: IVesselKeyMetricsDateBased[] | null
  loading: boolean
  error: Error | null
}

export interface IVesselKeyMetricsDateBased {
  sectorId: number,
  totalDistance: number,
  fuelConsumed: number,
  co2Emissions: number,
  co2EmissionsWtW: number,
  mainEngineEmissions: number,
  mainEnginePilotEmissions: number,
  auxiliaryEngineEmissions: number,
  boilerEmissions: number
}

// # endregion

// Slice name in the ...Slice.ts file
export const GREEN = 'green';

// Action types
export const actionTypes = {
    GET_MSI_ENVIROMENTAL_CREDENTIALS: `${GREEN}/getMsiEnvironmentalCredentialsRequestAction`,
    GET_GREEN_SUMMARY: `${GREEN}/getGreenSummaryRequestAction`,
    GET_ROLLING_AER_VALUES: `${GREEN}/getRollingAerValuesRequestAction`,
    GET_EMISSIONS_OF_ECA_SEA_AREAS: `${GREEN}/getEmissionsOfEcaSeaAreasRequestAction`,
    GET_COHORT_EMISSIONS_OF_ECA_SEA_AREAS: `${GREEN}/getCohortEmissionsOfEcaSeaAreasRequestAction`,
    GET_EMISSIONS_OF_OPERATIONAL_PHASES: `${GREEN}/getEmissionsOfOperationalPhasesRequestAction`,
    GET_COHORT_EMISSIONS_OF_OPERATIONAL_PHASES: `${GREEN}/getCohortEmissionsOfOperationalPhasesRequestAction`,
    GET_VESSEL_CII_ALIGNMENTS: `${GREEN}/getVesselCiiAlignmentsRequestAction`,
    GET_FLEET_CII_ALIGNMENT: `${GREEN}/getFleetCiiAlignmentRequestAction`,
    GET_VESSELS_ETS_AND_FUELEU_EMISSIONS: `${GREEN}/getVesselsEtsAndFuelEuEmissionsRequestAction`,
    GET_COHORT_VESSELS_ETS_AND_FUELEU_EMISSIONS: `${GREEN}/getCohortVesselsEtsAndFuelEuEmissionsRequestAction`,
    GET_VESSELS_MAIN_ENGINE_FUEL_CONSUMES: `${GREEN}/getVesselsMainEngineFuelConsumesRequestAction`,
    GET_COHORT_VESSELS_MAIN_ENGINE_FUEL_CONSUMES: `${GREEN}/getCohortVesselsMainEngineFuelConsumesRequestAction`,
    GET_VESSEL_KEY_METRICS_DATE_BASED: `${GREEN}/getVesselKeyMetricsDateBasedRequestAction`,
    GET_COHORT_VESSEL_KEY_METRICS_DATE_BASED: `${GREEN}/getCohortVesselKeyMetricsDateBasedRequestAction`
};
